import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { AlertCircle, Eye, EyeOff, Users, Hash, Mail, RefreshCw } from 'lucide-react';

// Cache clearing utility
const clearAllCaches = async () => {
  console.log('[CacheClear] Starting cache clear...');
  
  // 1. Unregister service workers
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const reg of registrations) {
      await reg.unregister();
      console.log('[CacheClear] Unregistered SW');
    }
  }
  
  // 2. Clear Cache Storage
  if ('caches' in window) {
    const names = await caches.keys();
    for (const name of names) {
      await caches.delete(name);
      console.log('[CacheClear] Deleted cache:', name);
    }
  }
  
  // 3. Clear IndexedDB
  if ('indexedDB' in window) {
    const databases = ['localforage', 'firebaseLocalStorageDb', 'workbox-expiration'];
    databases.forEach(dbName => {
      try {
        indexedDB.deleteDatabase(dbName);
      } catch(e) {
        // Ignore errors
      }
    });
  }
  
  // 4. Clear storage (keep token)
  const token = localStorage.getItem('token');
  localStorage.clear();
  if (token) localStorage.setItem('token', token);
  sessionStorage.clear();
  
  console.log('[CacheClear] All caches cleared');
};

export default function LoginPage() {
  const [loginMode, setLoginMode] = useState('its'); // 'its' or 'email' - ITS is primary
  const [email, setEmail] = useState('');
  const [itsId, setItsId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  
  const { login, loginWithITS } = useAuth();
  const navigate = useNavigate();

  // Load saved credentials on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedItsId = localStorage.getItem('rememberedItsId');
    const savedPassword = localStorage.getItem('rememberedPassword');
    const savedMode = localStorage.getItem('rememberedMode');
    
    if (savedEmail || savedItsId) {
      setRememberMe(true);
      if (savedMode) setLoginMode(savedMode);
      if (savedEmail) setEmail(savedEmail);
      if (savedItsId) setItsId(savedItsId);
      if (savedPassword) setPassword(savedPassword);
    }
  }, []);

  // Handle cache clear
  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      await clearAllCaches();
      // Force reload
      window.location.reload(true);
    } catch (e) {
      console.error('[CacheClear] Error:', e);
      window.location.reload(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (loginMode === 'its') {
        await loginWithITS(itsId, password);
      } else {
        await login(email, password);
      }
      
      // Save or clear credentials based on Remember Me
      if (rememberMe) {
        localStorage.setItem('rememberedMode', loginMode);
        if (loginMode === 'email') {
          localStorage.setItem('rememberedEmail', email);
          localStorage.removeItem('rememberedItsId');
        } else {
          localStorage.setItem('rememberedItsId', itsId);
          localStorage.removeItem('rememberedEmail');
        }
        localStorage.setItem('rememberedPassword', password);
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedItsId');
        localStorage.removeItem('rememberedPassword');
        localStorage.removeItem('rememberedMode');
      }
      
      navigate('/');
    } catch (err) {
      console.error('[Login] Error:', err);
      let errorMessage = 'Something went wrong';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        errorMessage = 'Cannot connect to server. Please check your internet connection or try clearing cache.';
      } else if (err.code === 'ECONNREFUSED') {
        errorMessage = 'Server is not responding. Please try again later.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Invalid credentials. Please check your email/ITS and password.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(30, 58, 138, 0.92) 0%, rgba(30, 58, 138, 0.8) 100%), url('https://images.unsplash.com/photo-1759267134451-f6224c7a40e5?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-navy/90 to-navy/70" />
      
      <Card className="w-full max-w-md relative z-10 glass shadow-2xl border-0 animate-slide-up" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <img 
              src="/logo.jpeg" 
              alt="Anjuman e Saifee" 
              className="w-20 h-20 rounded-xl object-cover mx-auto shadow-lg"
            />
          </div>
          <CardTitle className="text-2xl font-heading text-slate-800">
            Anjuman e Saifee
          </CardTitle>
          <CardDescription className="text-slate-600">
            Jamaat Management System
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm animate-fade-in" data-testid="error-message">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            
            {/* Login Mode Toggle - ITS is Primary */}
            <div className="flex gap-2 mb-4">
              <Button
                type="button"
                variant={loginMode === 'its' ? 'default' : 'outline'}
                className={`flex-1 ${loginMode === 'its' ? 'bg-navy' : ''}`}
                onClick={() => setLoginMode('its')}
              >
                <Hash className="w-4 h-4 mr-2" />
                ITS ID
              </Button>
              <Button
                type="button"
                variant={loginMode === 'email' ? 'default' : 'outline'}
                className={`flex-1 ${loginMode === 'email' ? 'bg-navy' : ''}`}
                onClick={() => setLoginMode('email')}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email
              </Button>
            </div>

            {loginMode === 'its' ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="itsId" className="form-label">ITS ID</Label>
                  <Input
                    id="itsId"
                    type="text"
                    placeholder="Enter your 8-digit ITS ID"
                    value={itsId}
                    onChange={(e) => setItsId(e.target.value)}
                    required
                    data-testid="its-input"
                    className="mt-1"
                    maxLength={8}
                  />
                  <p className="text-xs text-slate-500 mt-1">For Mumineen: Use ITS ID as password</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email" className="form-label">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                    className="mt-1"
                  />
                </div>
              </div>
            )}
            
            <div className="mt-4">
              <Label htmlFor="password" className="form-label">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {/* Remember Me Checkbox */}
            <div className="mt-4 flex items-center space-x-2">
              <Checkbox 
                id="rememberMe" 
                checked={rememberMe}
                onCheckedChange={setRememberMe}
                data-testid="remember-me-checkbox"
              />
              <Label 
                htmlFor="rememberMe" 
                className="text-sm text-slate-600 cursor-pointer select-none"
              >
                Remember me
              </Label>
            </div>
            
            <Button 
              type="submit" 
              className="w-full mt-6 bg-navy hover:bg-navy/90" 
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          {/* Clear Cache Button - for users experiencing loading issues */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <Button 
              type="button"
              variant="ghost"
              className="w-full text-slate-500 hover:text-slate-700 text-sm"
              onClick={handleClearCache}
              disabled={clearingCache}
              data-testid="clear-cache-button"
            >
              {clearingCache ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Clearing cache...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Having issues? Clear cache & reload
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
